import { format, isValid, parseISO } from 'date-fns';
import { getPayrollFrequencyOption } from '@/pages/SpreadsheetView/config/payrollFrequencyConfig';
import { selected1099KOptions } from './config/taxFileReady';
import { displayValue } from './api';

export interface FormattedCell {
  text: string;
  /** Secondary line, e.g. sales amount details */
  sub?: string;
  /** Employee who recorded the value (rendered as an initials avatar) */
  user?: string;
  kind: 'text' | 'boolean-true' | 'boolean-false' | 'empty';
}

const EMPTY: FormattedCell = { text: '—', kind: 'empty' };

/**
 * THE truthy check for workflow boolean flags (service flags, Ask My
 * Accountant). V1 and the V2 loader's clients join yield real booleans;
 * legacy/unlinked rows may hold Y/yes/true text instead. Every reader
 * (cell display, toggles, tab filtering, sync gates) must use this so a
 * legacy 'Y' row can't count as "has the service" in one place and render
 * as off in another.
 */
export function isTruthyFlag(value: unknown): boolean {
  return (
    value === true ||
    (typeof value === 'string' &&
      ['true', 'y', 'yes'].includes(value.trim().toLowerCase()))
  );
}

// V1 stores date_with_user / month_with_user as { date, username } (or a
// plain date string); assignee columns store employee NAMES (or special
// option strings), multi-assignee stores string[] or a comma-joined string.

function formatDateString(raw: string, monthOnly: boolean): string {
  const parsed = parseISO(raw.slice(0, 10));
  if (!isValid(parsed)) return raw;
  return format(parsed, monthOnly ? 'MM/yyyy' : 'MM/dd/yy');
}

function formatWithUser(value: unknown, monthOnly: boolean): FormattedCell {
  if (value == null || value === '') return EMPTY;
  if (typeof value === 'string') {
    return { text: formatDateString(value, monthOnly), kind: 'text' };
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    const obj = value as { date?: unknown; username?: unknown };
    const date = typeof obj.date === 'string' ? obj.date : null;
    const username =
      typeof obj.username === 'string' ? obj.username : undefined;
    if (!date) return EMPTY;
    return {
      text: formatDateString(date, monthOnly),
      user: username || undefined,
      kind: 'text',
    };
  }
  return EMPTY;
}

// V1 stores month_sales_data as { businessStatus, dateReported, amount,
// notes, estimatedTax, displayNote }; older rows may still hold the legacy
// {date, username} shape or a string

interface MonthSalesParts {
  amount: string | null;
  reported: string | null;
  status: string | null;
  notes: string | null;
  estimated: boolean;
  /** V1's "Display note in cell instead of date" flag (and a note exists) */
  showNote: boolean;
}

function monthSalesParts(obj: Record<string, unknown>): MonthSalesParts {
  const notes = typeof obj.notes === 'string' && obj.notes ? obj.notes : null;
  let rawDate: string | null = null;
  if (typeof obj.dateReported === 'string') rawDate = obj.dateReported;
  else if (typeof obj.date === 'string') rawDate = obj.date;
  return {
    amount:
      typeof obj.amount === 'number'
        ? obj.amount.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
          })
        : null,
    reported: rawDate ? formatDateString(rawDate, false) : null,
    status:
      typeof obj.businessStatus === 'string' && obj.businessStatus
        ? obj.businessStatus
        : null,
    notes,
    estimated: obj.estimatedTax === true,
    showNote: obj.displayNote === true && notes !== null,
  };
}

function formatMonthSalesData(value: unknown): FormattedCell {
  if (value == null || value === '') return EMPTY;
  if (typeof value === 'string') {
    return { text: formatDateString(value, false), kind: 'text' };
  }
  if (typeof value !== 'object' || Array.isArray(value)) return EMPTY;

  const p = monthSalesParts(value as Record<string, unknown>);
  const fallback =
    p.amount ?? p.reported ?? p.status ?? (p.estimated ? 'Estimated' : null);
  const text = p.showNote ? p.notes : fallback;
  if (!text) return EMPTY;
  // Everything informative that isn't already shown as the primary text
  const sub = [p.amount, p.reported, p.status, p.estimated ? 'Estimated' : null]
    .filter(part => part && part !== text)
    .join(' · ');
  return { text, sub: sub || undefined, kind: 'text' };
}

/** The Tax File Ready 1099K multi-toggle ({option: boolean} object): the
 *  first toggled option as the cell text, the rest as the sub line. */
function format1099K(value: unknown): FormattedCell {
  const selected = selected1099KOptions(value);
  if (selected.length === 0) return EMPTY;
  return {
    text: selected[0],
    sub: selected.length > 1 ? selected.slice(1).join(', ') : undefined,
    kind: 'text',
  };
}

function maskTaxId(value: unknown): FormattedCell {
  if (typeof value !== 'string' || !value.trim()) return EMPTY;
  const digits = value.replace(/\D/g, '');
  if (digits.length < 4) return { text: '•••••••••', kind: 'text' };
  return { text: `••-•••${digits.slice(-4)}`, kind: 'text' };
}

/**
 * Several workflow columns are declared 'text' but V1 writes objects into
 * them: month_sales_data shapes (e.g. Sales Tax Reported) and
 * { date, username } "timestamp" values. Detect by shape so the data shows
 * regardless of the declared column type. Returns null for non-objects.
 */
function formatUntypedObject(value: unknown): FormattedCell | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const obj = value as Record<string, unknown>;
  if (
    'businessStatus' in obj ||
    'dateReported' in obj ||
    'estimatedTax' in obj ||
    'amount' in obj
  ) {
    return formatMonthSalesData(obj);
  }
  if ('date' in obj) return formatWithUser(obj, false);
  return null;
}

export function formatCell(
  columnName: string,
  columnType: string,
  value: unknown
): FormattedCell {
  if (columnName === 'Federal Tax ID') return maskTaxId(value);
  // Stored as a real boolean (the V1 flag) though the column is declared text
  if (columnName === 'Ask My Accountant') columnType = 'boolean';

  switch (columnType) {
    case 'boolean': {
      if (value == null || value === '') return EMPTY;
      return isTruthyFlag(value)
        ? { text: '✓', kind: 'boolean-true' }
        : { text: '—', kind: 'boolean-false' };
    }
    case 'date_with_user':
      return formatWithUser(value, false);
    case 'month_with_user':
      return formatWithUser(value, true);
    case 'month_sales_data':
      return formatMonthSalesData(value);
    case 'doc_1099k':
      return format1099K(value);
    case 'payroll_frequency': {
      // Stored values are V1's option keys (e.g. 'SemiMonthly') — show labels
      const text = displayValue(value);
      if (!text) return EMPTY;
      return {
        text: getPayrollFrequencyOption(text)?.label ?? text,
        kind: 'text',
      };
    }
    default: {
      const objectCell = formatUntypedObject(value);
      if (objectCell) return objectCell;
      const text = displayValue(value);
      return text ? { text, kind: 'text' } : EMPTY;
    }
  }
}
